import type { BrowserOptions } from "@sentry/browser";
import * as Sentry from "@sentry/browser";
import { version } from "../../package.json";
import data from "../assets/notices.json";
import {
  ApiEndpoints,
  AwsRegionNames,
  CookieNames,
  FrameAccountConfigs,
  SENTRY_DSN,
  StorageKeys,
} from "./config.js";
import type { AppConfig } from "./app-core.js";
import { bootstrap } from "./app-core.js";

/**
 * App
 * Copyright 2025 ENERCALC, LLC
 * All rights reserved.
 *
 * Production entry for `index.html`. Initializes Sentry with the full
 * production options, builds the production {@link AppConfig}, and delegates all
 * orchestration to the shared `bootstrap` in `app-core.ts`.
 *
 * @author: ENERCALC, LLC
 * @version: 1.0.0
 */

/** @type {BrowserOptions} */
const sentryOptions: BrowserOptions = {
  dsn: SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [400, 401, 403, 404, 500, 502, 503, 504, 599],
      failedRequestTargets: ["https://eneractivtest.com/.*"],
    }),
  ],
  tracesSampleRate: 0.5,
  tracePropagationTargets: [/^https:\/\/eneractivtest\.com/],
  environment: process.env.NODE_ENV,
  release: process.env.CODEBUILD_RESOLVED_SOURCE_VERSION || process.env.GIT_SHA || version,
};

Sentry.init(sentryOptions);

const config: AppConfig = {
  awsRegionNames: AwsRegionNames,
  apiEndpoints: ApiEndpoints,
  cookieNames: CookieNames,
  frameAccountConfigs: FrameAccountConfigs,
  storageKeys: StorageKeys,
  notificationBanner: true,
  notices: data,
  // browserAdvisoryMessage intentionally omitted: index.html pre-renders the
  // advisory text, so browserDetection only reveals it (behavior unchanged).
};

bootstrap(config);
